import { ColorPicker } from '../ui/ColorPicker';
import { SYNC_PROFILES, isSyncMode, type SyncMode } from '../sync/HttpProvider';
import { PanelFrame } from './floating';
import { useBoard, usePaneStore, useYMap } from './context';
import type { NoteLook } from './noteLook';
import { setToolSettings, useToolSettings } from './toolSettings';
import { RENDER_MODES, type RenderMode } from './viewport/sharpness';

export type GridStyle = 'dots' | 'lines' | 'none';

/** Board-level look, stored in the document's `meta` map and synced with the board. */
export interface BoardMetaSettings {
  bg?: string | null;
  grid?: GridStyle;
  gridSize?: number;
  /** One of LOOKS ids; undefined = the app theme. */
  look?: string;
  accent?: string | null;
  customCss?: string;
  /** Downscale imported images whose longest side exceeds this many pixels; unset/0 keeps dimensions. */
  importMaxSide?: number | null;
  /** How new sticky notes look (paper, text style, size); set from a note's inspector. See noteLook.ts. */
  noteLook?: Partial<NoteLook> | null;
  /** How eagerly the board syncs (HttpProvider's SyncProfile); unset is 'balanced'. */
  sync?: SyncMode | null;
}

const SYNC_MODES: [SyncMode, string][] = [
  ['live', 'Live'],
  ['balanced', 'Balanced'],
  ['saver', 'Saver'],
];

/** "2–30 s" from milliseconds. */
const secs = (a: number, b: number) => `${a / 1000}–${b / 1000} s`;

const IMPORT_SIDES: [number, string][] = [
  [0, 'Keep'],
  [2048, '2048'],
  [4096, '4096'],
  [8192, '8192'],
];

/** Built-in retro looks (palettes applied to the whole pane). */
export const LOOKS = [
  { id: 'paper', name: 'Paper' },
  { id: 'ice', name: 'Ice' },
  { id: 'amber', name: 'Amber' },
  { id: 'phosphor', name: 'Phosphor' },
  { id: 'blueprint', name: 'Blueprint' },
] as const;

/** Strips the few CSS features that could reach outside the board or load remote content. */
export function sanitizeCss(css: string): string {
  return css
    .replace(/<\/?style[^>]*>/gi, '')
    .replace(/@import[^;]*;?/gi, '')
    .replace(/@charset[^;]*;?/gi, '')
    .replace(/expression\s*\(/gi, '(')
    .replace(/behavior\s*:/gi, '')
    .replace(/url\s*\(\s*['"]?\s*(javascript|data|http|https|\/\/)[^)]*\)/gi, 'none')
    .slice(0, 20000);
}

export const GRID_SIZES = [16, 24, 32, 48, 64, 96, 128];

const RENDER_HINTS: Record<RenderMode, string> = {
  sharp: 'The board is redrawn crisp every time you stop zooming.',
  balanced: 'The board is redrawn crisp when you stop zooming at 75 % or closer, and further out once things would look soft.',
  performance:
    'Zooming keeps the picture already drawn, which is the smoothest but can look soft; it is redrawn only once it would look very blurry. For slow devices and very full boards.',
};

export function BoardSettings({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const pane = usePaneStore();
  const meta = useYMap<unknown>(board.meta) as BoardMetaSettings;
  const set = (patch: BoardMetaSettings) => board.setMeta(patch as Record<string, unknown>);
  const grid = meta.grid ?? 'dots';
  const sync: SyncMode = isSyncMode(meta.sync) ? meta.sync : 'balanced';
  const render = useToolSettings((t) => t.render);
  const cssLines = meta.customCss ? meta.customCss.split('\n').length : 0;
  return (
    <PanelFrame kind="settings" title="Board settings" className="board-settings" onClose={onClose}>
      <div className="insp-body">
        <div className="insp-label">Look</div>
        <div className="btn-row">
          <button type="button" className={`btn${!meta.look ? ' is-active' : ''}`} onClick={() => set({ look: null as unknown as string })}>
            Theme
          </button>
          {LOOKS.map((l) => (
            <button key={l.id} type="button" className={`btn${meta.look === l.id ? ' is-active' : ''}`} onClick={() => set({ look: l.id })}>
              {l.name}
            </button>
          ))}
        </div>
        <div className="insp-label">Accent ({meta.accent ?? 'default'})</div>
        <ColorPicker value={meta.accent ?? 'none'} onChange={(c) => set({ accent: c === 'none' ? null : c })} allowNone />
        <div className="insp-label">Background ({meta.bg ? meta.bg : 'theme'})</div>
        <ColorPicker value={meta.bg ?? 'none'} onChange={(c) => set({ bg: c === 'none' ? null : c })} allowNone />
        <div className="insp-label">Grid</div>
        <div className="btn-row">
          {(['dots', 'lines', 'none'] as GridStyle[]).map((g) => (
            <button key={g} type="button" className={`btn${grid === g ? ' is-active' : ''}`} onClick={() => set({ grid: g })}>
              {g}
            </button>
          ))}
        </div>
        {grid !== 'none' && (
          <>
            <div className="insp-label">Grid spacing</div>
            <div className="btn-row">
              {GRID_SIZES.map((n) => (
                <button key={n} type="button" className={`btn${(meta.gridSize ?? 32) === n ? ' is-active' : ''}`} onClick={() => set({ gridSize: n })}>
                  {n}
                </button>
              ))}
            </div>
            <div className="insp-muted">Grid snapping (menu) uses this spacing.</div>
          </>
        )}
        <div className="insp-label">Imported images: longest side (px)</div>
        <div className="btn-row">
          {IMPORT_SIDES.map(([n, label]) => (
            <button
              key={n}
              type="button"
              className={`btn${(meta.importMaxSide ?? 0) === n ? ' is-active' : ''}`}
              onClick={() => set({ importMaxSide: n === 0 ? null : n })}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="insp-muted">
          Keep stores images at their full size; a cap downscales larger ones as they are imported to this board. Format and quality are a device
          setting (menu → Storage &amp; imports).
        </div>
        {board.remote && (
          <>
            <div className="insp-label">Sync</div>
            <div className="btn-row">
              {SYNC_MODES.map(([m, label]) => (
                <button
                  key={m}
                  type="button"
                  className={`btn${sync === m ? ' is-active' : ''}`}
                  title={`Checks for changes every ${secs(SYNC_PROFILES[m].pollMin, SYNC_PROFILES[m].pollMax)}`}
                  onClick={() => set({ sync: m === 'balanced' ? null : m })}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="insp-muted">
              How often this board checks for changes made elsewhere: every {secs(SYNC_PROFILES[sync].pollMin, SYNC_PROFILES[sync].pollMax)}, quick
              while changes keep arriving and slower while nothing does. Live suits working on it together at the same time, Saver sends the
              fewest requests. Coming back to the tab or window always checks at once. Applies to everyone on this board.
            </div>
          </>
        )}
        <div className="insp-label">Custom CSS (scoped to this board)</div>
        <div className="btn-row">
          <button type="button" className="btn" onClick={() => pane.getState().setCssEditorOpen(true)}>
            Edit board CSS…
          </button>
        </div>
        <div className="insp-muted">
          {cssLines ? `${cssLines} line${cssLines === 1 ? '' : 's'} of CSS. ` : 'None yet. '}
          The editor previews as you type and has a guide to everything you can style.
        </div>

        <div className="insp-section">This device</div>
        <div className="insp-label">Rendering</div>
        <div className="btn-row">
          {RENDER_MODES.map((m) => (
            <button key={m.id} type="button" className={`btn${render === m.id ? ' is-active' : ''}`} onClick={() => setToolSettings({ render: m.id })}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="insp-muted">{RENDER_HINTS[render]} Applies to every board on this device.</div>
      </div>
    </PanelFrame>
  );
}
